/*
 * Intex hero slideshow — <intex-hero-slideshow>, sections/intex-hero-slideshow.liquid.
 *
 * Slides are stacked and cross-faded by the stylesheet; this script only moves
 * the .is-active class and keeps the accessibility state in step:
 *
 * - inactive slides are inert and aria-hidden, so their links leave the tab
 *   order and the accessibility tree;
 * - the active dot carries aria-current;
 * - while autoplaying the slides region is aria-live="off", and "polite" once
 *   rotation stops, so manual changes are announced but timed ones are not.
 *
 * Autoplay (data-autoplay, every data-speed seconds) pauses while something
 * inside the section has keyboard focus, while the tab is hidden, and while a
 * slide block is selected in the theme editor. The pause button stops it for
 * good. prefers-reduced-motion turns autoplay off entirely.
 *
 * It deliberately does not pause on hover: the banner is large, so a resting
 * mouse sat over it and autoplay looked broken. Focus only counts when it is
 * :focus-visible (keyboard). A mouse click leaves the clicked arrow or dot
 * focused in Chrome, and that focus would otherwise hold rotation until
 * something outside the slideshow was clicked.
 *
 * Input: prev / next / dot buttons, Left / Right arrow keys anywhere inside the
 * section, and a horizontal swipe on touch screens.
 */
(() => {
  if (customElements.get('intex-hero-slideshow')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SWIPE_MIN = 40;

  class IntexHeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-ihs-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-ihs-dot]'));
      this.region = this.querySelector('.ihs-slides');
      this.toggleButton = this.querySelector('[data-ihs-toggle]');
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.speed = Math.max(3, Number(this.dataset.speed) || 6) * 1000;
      this.wantsAutoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.holds = new Set();
      this.timer = null;

      this.update();
      if (this.slides.length < 2) return;

      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };

      this.querySelector('[data-ihs-prev]')?.addEventListener('click', () => this.go(this.index - 1), opts);
      this.querySelector('[data-ihs-next]')?.addEventListener('click', () => this.go(this.index + 1), opts);
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.go(Number(dot.dataset.ihsDot)), opts);
      });
      this.toggleButton?.addEventListener('click', () => this.toggleUserPause(), opts);

      this.addEventListener('keydown', (e) => this.onKey(e), opts);

      this.addEventListener('focusin', (e) => {
        if (e.target.matches(':focus-visible')) this.hold('focus');
        else this.release('focus');
      }, opts);
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.release('focus');
      }, opts);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.hold('hidden');
        else this.release('hidden');
      }, opts);
      reducedMotion.addEventListener('change', () => this.schedule(), opts);

      this.region.addEventListener('touchstart', (e) => this.onTouchStart(e), { passive: true, signal: this.abort.signal });
      this.region.addEventListener('touchend', (e) => this.onTouchEnd(e), { passive: true, signal: this.abort.signal });

      this.addEventListener('shopify:block:select', (e) => {
        const i = this.slides.indexOf(e.target.closest('[data-ihs-slide]'));
        if (i < 0) return;
        this.hold('editor');
        this.go(i);
      }, opts);
      this.addEventListener('shopify:block:deselect', () => this.release('editor'), opts);

      if (document.hidden) this.holds.add('hidden');
      this.schedule();
    }

    disconnectedCallback() {
      this.abort?.abort();
      clearTimeout(this.timer);
    }

    get autoplaying() {
      return this.wantsAutoplay && !reducedMotion.matches && !this.classList.contains('is-paused-by-user');
    }

    go(i) {
      const n = this.slides.length;
      const next = ((i % n) + n) % n;
      if (next !== this.index) {
        this.index = next;
        this.update();
      }
      this.schedule();
    }

    update() {
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.toggleAttribute('inert', !active);
        if (active) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      this.dots.forEach((dot, i) => {
        if (i === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      const active = this.slides[this.index];
      const type = ['card', 'splash', 'feature'].find((t) => active?.classList.contains(`ihs-slide--${t}`));
      if (type) this.dataset.accent = type;

      /* Start loading the following slide's photo now, so it is ready before
         it fades in. */
      const upcoming = this.slides[(this.index + 1) % this.slides.length];
      upcoming?.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });

    }

    hold(reason) {
      this.holds.add(reason);
      this.schedule();
    }

    release(reason) {
      this.holds.delete(reason);
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      this.timer = null;
      this.region?.setAttribute('aria-live', this.autoplaying ? 'off' : 'polite');
      if (!this.autoplaying || this.holds.size) return;
      this.timer = setTimeout(() => this.go(this.index + 1), this.speed);
    }

    toggleUserPause() {
      const paused = this.classList.toggle('is-paused-by-user');
      const btn = this.toggleButton;
      btn.setAttribute('aria-label', paused ? btn.dataset.labelPlay : btn.dataset.labelPause);
      this.schedule();
    }

    onKey(e) {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      this.go(this.index + (e.key === 'ArrowRight' ? 1 : -1));
      /* Keep focus on a control that still exists in the tab order: a link
         inside the slide that just went inert would otherwise drop focus. */
      if (!this.querySelector('.ihs-controls')?.contains(document.activeElement)) {
        this.dots[this.index]?.focus();
      }
    }

    onTouchStart(e) {
      const t = e.changedTouches[0];
      this.touch = { x: t.clientX, y: t.clientY };
    }

    onTouchEnd(e) {
      if (!this.touch) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - this.touch.x;
      const dy = t.clientY - this.touch.y;
      this.touch = null;
      if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy) * 1.2) return;
      this.go(this.index + (dx < 0 ? 1 : -1));
    }
  }

  customElements.define('intex-hero-slideshow', IntexHeroSlideshow);
})();